import { useEffect, useRef, useState } from "react";
import { AppState, type AppStateStatus } from "react-native";
import { useDispatch, useSelector } from "react-redux";
import * as Sentry from "@sentry/react-native";
import { actions as authActions } from "@reducers/authReducer";
import analytics from "@services/analytics";
import { FACE_CAPTURE_EVENTS } from "@constants/analyticsEventsCT";
import { appVersion, appVersionCode, baseURL } from "@config";
import getTokenZ from "@utils/getTokenZ";
import {
  buildNativeFaceCaptureConfig,
  dismissNativeFaceCapture,
  hasCameraPermission,
  isCameraPermissionError,
  isNoActivityError,
  launchNativeFaceCapture,
  updateNativeFaceCapture,
  type FaceCaptureReduxState,
} from "@utils/nativeFaceCapture";
import {
  faceCaptureSettled,
  isFaceCaptureInFlight,
} from "@utils/faceCaptureInFlight";

interface AuthSlice {
  userToken?: string | null;
  face_capture?: FaceCaptureReduxState;
}

interface RootStateShape {
  auth: AuthSlice;
}

const NO_ACTIVITY_RETRY_MS = 1000;
const EMPTY_FACE_CAPTURE: FaceCaptureReduxState = {};

const errorMessage = (error: unknown): string =>
  error instanceof Error ? error.message : String(error);

/**
 * Renders nothing. Watches the same `auth.face_capture` state the RN modal
 * self-gated on and, when the backend asks for a capture while the user is
 * logged in and the app is in the foreground, hands off to
 * FaceCaptureActivity. The activity owns camera, detection and upload; this
 * component only translates its outcome back into the Redux + analytics
 * contract the rest of the app already relies on.
 *
 * Redux stays the source of truth for the whole session, as it was for the
 * RN modal: while a launch is in flight, changes to `canCloseFaceCapture`
 * and `faceCaptureText` are pushed into the screen, and `faceCaptureVisible`
 * going false dismisses it (the screen itself refuses only mid-upload).
 *
 * When the DB closes the screen themselves (cross or back, only possible with
 * canCloseFaceCapture) the capture is simply dismissed and the screen that asked
 * for it is left in place — closing never navigates. A backend dismissal or a
 * successful upload also leaves navigation alone.
 *
 * It never asks for the CAMERA permission. PermissionsGate collects it at
 * boot; if it is missing here (denied, or revoked later in Settings) the
 * request simply stays in Redux and is retried the next time the app comes
 * to the foreground, by which point the gate has had its turn.
 */
export function NativeFaceCaptureController() {
  const dispatch = useDispatch();
  const userToken = useSelector(
    (state: RootStateShape) => state.auth?.userToken,
  );
  const faceCapture =
    useSelector((state: RootStateShape) => state.auth?.face_capture) ??
    EMPTY_FACE_CAPTURE;
  const {
    faceCaptureVisible,
    canCloseFaceCapture,
    faceCaptureFlow,
    faceCaptureText,
  } = faceCapture;

  // React Native reports "unknown" (Android: "uninitialized") until the first
  // lifecycle event, and a cold start that is already in the foreground may
  // never emit a change. Only a positive background/inactive report blocks.
  const [appState, setAppState] = useState<AppStateStatus>(
    AppState.currentState,
  );
  const appActive = appState !== "background" && appState !== "inactive";
  const [retryTick, setRetryTick] = useState(0);
  const inFlight = useRef(false);
  const latestFaceCapture = useRef(faceCapture);
  latestFaceCapture.current = faceCapture;

  useEffect(() => {
    const subscription = AppState.addEventListener(
      "change",
      (state: AppStateStatus) => setAppState(state),
    );
    return () => subscription.remove();
  }, []);

  useEffect(() => {
    // `inFlight` guards this instance; faceCaptureSettled() guards the
    // process. A remounted controller (host activity recreated on cold start)
    // must not launch over the capture the previous instance started — that
    // launch's callbacks still run and update Redux when it settles.
    if (!faceCaptureVisible || !userToken || !appActive || inFlight.current)
      return;
    const othersCapture = faceCaptureSettled();
    if (othersCapture) {
      // Someone else's capture (a previous controller instance, or the KYC
      // selfie tile) holds the module. Nothing else re-runs this effect when
      // it ends, so wait for it and look at the request again then.
      let cancelled = false;
      othersCapture.then(() => {
        if (!cancelled) setRetryTick((tick) => tick + 1);
      });
      return () => {
        cancelled = true;
      };
    }
    inFlight.current = true;
    let retryTimer: ReturnType<typeof setTimeout> | undefined;
    const eventProps = {
      canCloseFaceCapture,
      faceCaptureVisible,
      faceCaptureFlow,
      source: "native",
    };

    (async () => {
      if (!(await hasCameraPermission())) {
        // Not ours to prompt for (PermissionsGate owns CAMERA). Leave the
        // request in Redux; the appState dependency re-runs this effect when
        // the app comes back. Say so in analytics — a capture that never
        // appears is otherwise indistinguishable from one never requested.
        analytics.trackEvent(FACE_CAPTURE_EVENTS.FACE_CAPTURE_MODAL_BLOCKED, {
          ...eventProps,
          reason: "camera_permission",
        });
        return;
      }
      const token = await getTokenZ();
      const config = buildNativeFaceCaptureConfig({
        faceCapture: latestFaceCapture.current,
        token,
        baseUrl: baseURL,
        appVersion,
        appVersionCode,
      });

      analytics.trackEvent(
        FACE_CAPTURE_EVENTS.FACE_CAPTURE_MODAL_OPEN,
        eventProps,
      );

      try {
        const result = await launchNativeFaceCapture(config);
        dispatch(
          authActions.setFaceCaptureData({
            faceCaptureVisible: false,
            isFaceCaptureUploading: false,
          }),
        );
        if (result.status === "uploaded") {
          analytics.trackEvent(
            FACE_CAPTURE_EVENTS.FACE_CAPTURE_MODAL_SUBMIT,
            eventProps,
          );
        } else if (result.status === "closed") {
          analytics.trackEvent(
            FACE_CAPTURE_EVENTS.FACE_CAPTURE_MODAL_CLOSE,
            eventProps,
          );
        }
        // "dismissed" is the backend withdrawing the request — the RN modal
        // fired no event for that either.
      } catch (error) {
        if (isNoActivityError(error)) {
          // Cold start or an activity transition — React has no foreground
          // activity yet. The capture request is still in Redux; try again.
          retryTimer = setTimeout(
            () => setRetryTick((tick) => tick + 1),
            NO_ACTIVITY_RETRY_MS,
          );
          return;
        }
        if (isCameraPermissionError(error)) {
          // Lost the race with a revoke between our check and the launch.
          // Same handling as a failed pre-check: wait for the gate.
          return;
        }
        analytics.trackEvent(FACE_CAPTURE_EVENTS.FACE_CAPTURE_MODAL_ERROR, {
          ...eventProps,
          error: errorMessage(error),
        });
        Sentry.captureException(
          error instanceof Error ? error : new Error(errorMessage(error)),
        );
        // No fallback screen exists any more. Close the request rather than
        // loop on it; the backend re-sends face_capture on its next response.
        dispatch(
          authActions.setFaceCaptureData({
            faceCaptureVisible: false,
            isFaceCaptureUploading: false,
          }),
        );
      } finally {
        inFlight.current = false;
      }
    })();

    return () => {
      if (retryTimer) clearTimeout(retryTimer);
    };
  }, [
    faceCaptureVisible,
    userToken,
    appActive,
    retryTick,
    canCloseFaceCapture,
    faceCaptureFlow,
    dispatch,
  ]);

  // Keep the running screen in step with Redux. The launch effect above
  // captured a snapshot; anything the backend changes afterwards goes here.
  useEffect(() => {
    // Any auto-capture in flight, not just this instance's: after a remount
    // the running screen was launched by the previous instance and still has
    // to hear about a dismiss or sign-out. Never the KYC tile's capture.
    if (!inFlight.current && !isFaceCaptureInFlight("auto")) return;
    // The backend withdrew the request, or the session ended (sign-out, or a
    // 401 anywhere): the screen has no business staying open either way.
    if (faceCaptureVisible === false || !userToken) {
      dismissNativeFaceCapture();
      return;
    }
    updateNativeFaceCapture({
      canClose: Boolean(canCloseFaceCapture),
      text: typeof faceCaptureText === "string" ? faceCaptureText.trim() : "",
    });
  }, [faceCaptureVisible, canCloseFaceCapture, faceCaptureText, userToken]);

  return null;
}
